import {
  Calendar,
  Clock,
  MapPin,
  Users,
  Star,
  Share2,
  Heart,
  Ticket,
  Info,
  Shield,
  RefreshCw,
} from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";

import GroundMap from "../../../components/maps/ground-map/GroundMap";
import { Avatar, AvatarFallback, AvatarImage } from "../../../components/ui/avatar";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Skeleton } from "../../../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import WeatherWidget from "../../../components/weather/weather-widget/WeatherWidget";
import { useAuth } from "../../../hooks/useAuth";
import { useGetEventByIdQuery } from "../../../store/api/eventApi";
import { ROUTES } from "../../../utils/constants";
import { formatCurrency, formatDateTime, formatEventDuration } from "../../../utils/helpers";

const EventDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [isFavorite, setIsFavorite] = useState(false);

  const { data: eventData, isLoading, error } = useGetEventByIdQuery(id!);
  const event = eventData?.data;

  const handleBookTickets = () => {
    if (!isAuthenticated) {
      navigate(ROUTES.LOGIN, { state: { from: { pathname: `/events/${id}` } } });
      return;
    }
    navigate(`${ROUTES.SEAT_SELECTION}/${id}`);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: event?.title || "",
          text: event?.description || "",
          url: window.location.href,
        });
      } catch (error) {
        console.error("Error sharing:", error);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
    // This would make an API call to add/remove from favorites
  };

  if (isLoading) {
    return (
      <div className='container py-8'>
        <div className='mx-auto max-w-4xl space-y-6'>
          <Skeleton className='aspect-video' />
          <div className='space-y-4'>
            <Skeleton className='h-8 w-3/4' />
            <Skeleton className='h-4 w-1/2' />
            <Skeleton className='h-20' />
          </div>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className='container py-8'>
        <div className='text-center'>
          <h1 className='mb-4 text-2xl font-bold'>Event Not Found</h1>
          <p className='mb-6 text-muted-foreground'>
            The event you&apos;re looking for doesn&apos;t exist or has been cancelled.
          </p>
          <Button asChild>
            <Link to={ROUTES.EVENTS}>Browse All Events</Link>
          </Button>
        </div>
      </div>
    );
  }

  const isEventActive = event.status === "active";
  const isEventCompleted = event.status === "completed";
  const isEventCancelled = event.status === "cancelled";

  return (
    <div className='container py-8'>
      <div className='mx-auto max-w-6xl'>
        <div className='grid grid-cols-1 gap-8 lg:grid-cols-3'>
          {/* Main Content */}
          <div className='space-y-6 lg:col-span-2'>
            {/* Hero Image */}
            <div className='relative aspect-video overflow-hidden rounded-lg bg-gradient-to-r from-blue-500 to-purple-600'>
              <div className='absolute left-4 top-4'>
                <Badge
                  variant={
                    isEventActive ? "default" : isEventCompleted ? "secondary" : "destructive"
                  }
                >
                  {event.status}
                </Badge>
              </div>
              <div className='absolute right-4 top-4 flex gap-2'>
                <Button
                  variant='secondary'
                  size='sm'
                  onClick={toggleFavorite}
                  className={isFavorite ? "text-red-500" : ""}
                >
                  <Heart className={`h-4 w-4 ${isFavorite ? "fill-current" : ""}`} />
                </Button>
                <Button variant='secondary' size='sm' onClick={handleShare}>
                  <Share2 className='h-4 w-4' />
                </Button>
              </div>
            </div>

            {/* Event Info */}
            <div>
              <div className='mb-4 flex items-start justify-between'>
                <div className='flex-1'>
                  <h1 className='mb-2 text-3xl font-bold'>{event.title}</h1>
                  <p className='mb-4 text-muted-foreground'>{event.description}</p>

                  <div className='grid grid-cols-1 gap-4 text-sm md:grid-cols-2'>
                    <div className='flex items-center gap-2'>
                      <Calendar className='h-4 w-4 text-muted-foreground' />
                      <span>{formatDateTime(event.datetime)}</span>
                    </div>
                    <div className='flex items-center gap-2'>
                      <Clock className='h-4 w-4 text-muted-foreground' />
                      <span>{formatEventDuration(event.duration)} duration</span>
                    </div>
                    <div className='flex items-center gap-2'>
                      <MapPin className='h-4 w-4 text-muted-foreground' />
                      <span>
                        {event.ground?.location.city}, {event.ground?.location.country}
                      </span>
                    </div>
                    <div className='flex items-center gap-2'>
                      <Users className='h-4 w-4 text-muted-foreground' />
                      <span>{event.ground?.capacity.toLocaleString()} capacity</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Organizer Info */}
              <Card className='mb-6'>
                <CardContent className='p-4'>
                  <div className='flex items-center gap-3'>
                    <Avatar>
                      <AvatarImage src={event.organizer?.profile.avatar || "/placeholder.svg"} />
                      <AvatarFallback>{event.organizer?.profile.name.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className='font-medium'>Organized by {event.organizer?.profile.name}</p>
                      <div className='flex items-center gap-1 text-sm text-muted-foreground'>
                        <Star className='h-3 w-3 fill-yellow-400 text-yellow-400' />
                        <span>4.8 (156 events)</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tabs */}
            <Tabs defaultValue='tickets' className='w-full'>
              <TabsList className='grid w-full grid-cols-4'>
                <TabsTrigger value='tickets'>Tickets</TabsTrigger>
                <TabsTrigger value='details'>Details</TabsTrigger>
                <TabsTrigger value='venue'>Venue</TabsTrigger>
                <TabsTrigger value='weather'>Weather</TabsTrigger>
              </TabsList>

              <TabsContent value='tickets' className='space-y-4'>
                <Card>
                  <CardHeader>
                    <CardTitle className='flex items-center gap-2'>
                      <Ticket className='h-5 w-5' />
                      Available Tickets
                    </CardTitle>
                  </CardHeader>
                  <CardContent className='space-y-4'>
                    {event.ticketTypes.map(ticketType => (
                      <div
                        key={ticketType.id}
                        className='flex items-center justify-between rounded-lg border p-4'
                      >
                        <div>
                          <h4 className='font-medium'>{ticketType.name}</h4>
                          <p className='text-sm text-muted-foreground'>{ticketType.description}</p>
                          <p className='mt-1 text-xs text-muted-foreground'>
                            {ticketType.available} of {ticketType.quantity} available
                          </p>
                        </div>
                        <div className='text-right'>
                          <div className='text-lg font-semibold'>
                            {formatCurrency(ticketType.price)}
                          </div>
                          <Badge variant={ticketType.available > 0 ? "default" : "secondary"}>
                            {ticketType.available > 0 ? "Available" : "Sold Out"}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value='details'>
                <Card>
                  <CardHeader>
                    <CardTitle className='flex items-center gap-2'>
                      <Info className='h-5 w-5' />
                      Event Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className='space-y-4'>
                    <div>
                      <h4 className='mb-2 font-medium'>About This Event</h4>
                      <p className='text-muted-foreground'>{event.description}</p>
                    </div>

                    <div>
                      <h4 className='mb-2 font-medium'>Event Rules & Guidelines</h4>
                      <ul className='space-y-1 text-sm text-muted-foreground'>
                        <li>• Gates open 1 hour before event start time</li>
                        <li>• No outside food or beverages allowed</li>
                        <li>• Smoking is prohibited in the venue</li>
                        <li>• Children under 12 must be accompanied by an adult</li>
                        <li>• Event may be cancelled due to weather conditions</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className='mb-2 font-medium'>Refund Policy</h4>
                      <div className='flex items-start gap-2 text-sm text-muted-foreground'>
                        <Shield className='mt-0.5 h-4 w-4 flex-shrink-0' />
                        <p>
                          Full refund available up to 24 hours before the event. No refunds after
                          this time unless the event is cancelled by the organizer.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value='venue'>
                <Card>
                  <CardHeader>
                    <CardTitle>Venue Information</CardTitle>
                  </CardHeader>
                  <CardContent className='space-y-4'>
                    <div>
                      <h4 className='mb-2 font-medium'>{event.ground?.name}</h4>
                      <p className='text-muted-foreground'>
                        {event.ground?.location.address}
                        <br />
                        {event.ground?.location.city}, {event.ground?.location.country}
                      </p>
                    </div>

                    <div>
                      <h4 className='mb-2 font-medium'>Amenities</h4>
                      <div className='flex flex-wrap gap-2'>
                        {event.ground?.amenities.map((amenity, index) => (
                          <Badge key={index} variant='secondary'>
                            {amenity}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {event.ground && (
                      <GroundMap
                        coordinates={event.ground.location.coordinates}
                        name={event.ground.name}
                        address={event.ground.location.address}
                      />
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value='weather'>
                <Card>
                  <CardHeader>
                    <CardTitle>Weather Forecast</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {event.ground && (
                      <WeatherWidget
                        coordinates={event.ground.location.coordinates}
                        eventDate={event.datetime}
                      />
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className='space-y-6'>
            {/* Booking Card */}
            <Card>
              <CardHeader>
                <CardTitle>Book Your Tickets</CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div className='text-center'>
                  <div className='text-2xl font-bold'>
                    From {formatCurrency(Math.min(...event.ticketTypes.map(t => t.price)))}
                  </div>
                  <div className='text-sm text-muted-foreground'>per ticket</div>
                </div>

                <Button
                  className='w-full'
                  size='lg'
                  onClick={handleBookTickets}
                  disabled={isEventCompleted || isEventCancelled}
                >
                  {isEventCompleted
                    ? "Event Completed"
                    : isEventCancelled
                      ? "Event Cancelled"
                      : "Select Seats"}
                </Button>

                {!isAuthenticated && (
                  <p className='text-center text-xs text-muted-foreground'>
                    <Link to={ROUTES.LOGIN} className='text-primary hover:underline'>
                      Sign in
                    </Link>{" "}
                    to book tickets
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card>
              <CardHeader>
                <CardTitle>Event Stats</CardTitle>
              </CardHeader>
              <CardContent className='space-y-3'>
                <div className='flex justify-between'>
                  <span className='text-sm text-muted-foreground'>Total Capacity</span>
                  <span className='font-medium'>{event.ground?.capacity.toLocaleString()}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-sm text-muted-foreground'>Tickets Sold</span>
                  <span className='font-medium'>
                    {event.ticketTypes.reduce((total, t) => total + (t.quantity - t.available), 0)}
                  </span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-sm text-muted-foreground'>Availability</span>
                  <span className='font-medium'>
                    {Math.round(
                      (event.ticketTypes.reduce((total, t) => total + t.available, 0) /
                        event.ticketTypes.reduce((total, t) => total + t.quantity, 0)) *
                        100
                    )}
                    %
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Similar Events */}
            <Card>
              <CardHeader>
                <CardTitle>You Might Also Like</CardTitle>
              </CardHeader>
              <CardContent>
                <div className='space-y-3'>
                  {/* This would show similar events */}
                  <div className='py-4 text-center text-muted-foreground'>
                    <RefreshCw className='mx-auto mb-2 h-8 w-8 opacity-50' />
                    <p className='text-sm'>Loading similar events...</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetails;
